import { IconSource } from '@prenuvo/halo';
import { QuestionCircle } from '@prenuvo/halo-icon';

import {
  CARE_PLAN_SERVICE_KEY,
  CARE_SERVICE_ORGANS_PARENT_KEY,
  CarePlanService,
  CareServiceOrgansKey,
  CareServiceOrgansSubKey,
  CareServiceReportOrgan,
  CareServiceReportOrgansStructure,
} from '@/api';
import {
  CARE_SERVICE_REPORT_LINK_KEY,
  CareServiceReportLink,
  CareServiceReportOrganItem,
} from '@/shared/model';
import { isNotEmpty } from '@/shared/util';

import {
  CARE_SERVICE_ORGAN_FULL_LIST,
  NAV_ICON_MAP,
  NavIconKey,
  NECK_DESIRED_ORDER,
  OTHER_ORGANS,
} from './care-plan-service.constant';

export const mapSubLinks = (
  nodes: CareServiceReportOrganItem[],
  disabled?: boolean,
): CareServiceReportLink[] =>
  nodes.map(({ key, sub, value }) => {
    let children = isNotEmpty(sub) ? mapSubLinks(sub, disabled) : [];

    if (key === CARE_SERVICE_ORGANS_PARENT_KEY.NECK && children?.length) {
      children = children
        .slice()
        .sort(
          (a, b) =>
            NECK_DESIRED_ORDER.indexOf(a.key as CareServiceOrgansSubKey) -
            NECK_DESIRED_ORDER.indexOf(b.key as CareServiceOrgansSubKey),
        );
    }

    return {
      key,
      disabled,
      icon: (NAV_ICON_MAP[key as NavIconKey] ?? QuestionCircle) as IconSource,
      progress: null,
      sub: children,
      title: value,
    };
  });

interface GenerateOrganLinksArgs {
  isBodyComposition: boolean;
  isQualityReviewApplicable: boolean;
  structure: CareServiceReportOrganItem[];
}

export const generateOrganLinks = ({
  isBodyComposition = false,
  isQualityReviewApplicable,
  structure,
}: GenerateOrganLinksArgs): CareServiceReportLink[] => [
  {
    key: CARE_SERVICE_REPORT_LINK_KEY.REASON_FOR_SCAN,
    icon: NAV_ICON_MAP.reason_for_scan,
    progress: null,
    title: 'Reason for a scan',
  },
  ...(isQualityReviewApplicable
    ? [
        {
          key: CARE_SERVICE_REPORT_LINK_KEY.QUALITY_REVIEW,
          icon: NAV_ICON_MAP.quality_review,
          progress: null,
          title: 'Quality review',
        },
      ]
    : []),
  ...(!isBodyComposition
    ? [
        {
          key: CARE_SERVICE_REPORT_LINK_KEY.AI_FINDINGS,
          icon: NAV_ICON_MAP.ai_findings,
          progress: null,
          title: 'AI findings',
        },
      ]
    : []),
  ...structure.map(({ key, disabled, sub, value: title }) => ({
    key,
    disabled,
    icon: NAV_ICON_MAP[key as NavIconKey] || QuestionCircle,
    progress: null,
    sub: sub ? mapSubLinks(sub, disabled) : [],
    title,
  })),
  {
    key: CARE_SERVICE_REPORT_LINK_KEY.KEY_IMAGES,
    icon: NAV_ICON_MAP.key_images,
    progress: null,
    title: 'Key images',
  },
  {
    key: CARE_SERVICE_REPORT_LINK_KEY.FINAL_IMPRESSIONS,
    icon: NAV_ICON_MAP.final_impressions,
    progress: null,
    title: 'Final impressions',
  },
  {
    key: CARE_SERVICE_REPORT_LINK_KEY.PREVIEW,
    icon: NAV_ICON_MAP.preview,
    progress: null,
    title: 'Preview report',
  },
];

export const sortNeckSubOrgans = (subs: CareServiceReportOrganItem[]) =>
  [...subs].sort(
    (a, b) =>
      NECK_DESIRED_ORDER.indexOf(a.key as CareServiceOrgansSubKey) -
      NECK_DESIRED_ORDER.indexOf(b.key as CareServiceOrgansSubKey),
  );

export const getCareServiceReportOrganItem = (
  organItem: CareServiceReportOrgan,
  disabled = false,
): CareServiceReportOrganItem => {
  let sub = (organItem.subOrgans ?? []).map((s) => ({
    key: s.key as CareServiceOrgansKey,
    value: s.displayName,
    ...(disabled ? { disabled: true } : {}),
  }));

  if (organItem.key === CARE_SERVICE_ORGANS_PARENT_KEY.NECK) {
    sub = sortNeckSubOrgans(sub);
  }

  return {
    key: organItem.key as CareServiceOrgansKey,
    disabled,
    value: organItem.displayName,
    ...(isNotEmpty(sub) ? { sub } : {}),
  };
};

export function parseCarePlanToOrgans(carePlanOrgans: CareServiceReportOrgansStructure): {
  structure: CareServiceReportOrganItem[];
  uuid: string;
} {
  const { disabledOrgans = [], enabledOrgans = [], uuid } = carePlanOrgans;

  const existingItemsMap = new Map(enabledOrgans.map((item) => [item.key, item]));
  const disabledItemsMap = new Map(disabledOrgans.map((item) => [item.key, item]));

  const enabledStructure: CareServiceReportOrganItem[] = CARE_SERVICE_ORGAN_FULL_LIST.filter(
    (item) => existingItemsMap.has(item.key),
  ).map((item) => getCareServiceReportOrganItem(existingItemsMap.get(item.key)!));

  const disabledStructure: CareServiceReportOrganItem[] = CARE_SERVICE_ORGAN_FULL_LIST.filter(
    (item) => disabledItemsMap.has(item.key),
  ).map((item) => getCareServiceReportOrganItem(disabledItemsMap.get(item.key)!, true));

  const structure = isNotEmpty(disabledStructure)
    ? [
        ...enabledStructure,
        {
          key: CARE_SERVICE_ORGANS_PARENT_KEY.OTHER_ORGANS,
          disabled: true,
          sub: disabledStructure,
          value: OTHER_ORGANS,
        },
      ]
    : enabledStructure;

  return {
    structure,
    uuid,
  };
}

export const hasOnlyBodyCompositionService = (services: CarePlanService[]): boolean =>
  services.length === 1 &&
  services[0].serviceKey === CARE_PLAN_SERVICE_KEY.IMAGING_BASE_BODY_COMPOSITION;
